import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useTutorials } from "_shared/tutorials/tutorial-provider";
import { loadContentsCollapsed, saveContentsCollapsed } from "knowledge-base/_views/knowledge-base-contents-preference";
import {
  forgetBuildYourTeamDismissal,
  readBuildYourTeamDismissal,
} from "legal-entities/invitations/bulk/_views/build-your-team-banner";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useState } from "react";
import SettingsSection from "users/settings/_views/settings-section";

export const TUTORIAL_PROGRESS_RESET_MESSAGE = "Tutorial progress is reset. Each tip shows again.";
export const DISMISSED_TIPS_SHOWN_MESSAGE = "Dismissed tips and panels show again.";

function hasDismissedTips(): boolean {
  return readBuildYourTeamDismissal(globalThis.localStorage) || loadContentsCollapsed();
}

/**
 * Tutorials, and the tips and panels a person has put away.
 *
 * The tutorial controls are the same as those on Help, which keeps them: Help is where a person
 * looking for a tour goes, and Settings is where a person looking to turn tours off goes.
 */
export default function TutorialSettingsSection() {
  const { disableTutorials, enableTutorials, resetTutorials, tutorialsEnabled } = useTutorials();
  const [tipsDismissed, setTipsDismissed] = useState(hasDismissedTips);

  // Confirmed only once it is saved. A failed reset is rolled back and reported by the provider,
  // and a success shown before that would contradict it.
  const resetProgress = async () => {
    if (await resetTutorials()) {
      enqueueSnackbar(TUTORIAL_PROGRESS_RESET_MESSAGE, { variant: "success" });
    }
  };

  const showDismissedTips = () => {
    forgetBuildYourTeamDismissal(globalThis.localStorage);
    saveContentsCollapsed(false);
    setTipsDismissed(false);
    enqueueSnackbar(DISMISSED_TIPS_SHOWN_MESSAGE, { variant: "success" });
  };

  return (
    <SettingsSection
      description="Guided tours and tips. Your account keeps the tutorial settings; this browser keeps the tips and panels you dismissed."
      title="Tutorials and tips"
    >
      <FormControlLabel
        control={
          <Switch
            checked={tutorialsEnabled}
            onChange={(_event, checked) => (checked ? enableTutorials() : disableTutorials())}
          />
        }
        label="Show tutorials"
      />
      <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
        <Typography color="text.secondary" variant="body2">
          Show every tutorial tip again, including the ones you finished or closed.
        </Typography>
        <Button onClick={() => void resetProgress()} variant="outlined">Reset tutorial progress</Button>
      </Stack>
      <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
        <Typography color="text.secondary" variant="body2">
          {tipsDismissed
            ? "Bring back the \"Build your team\" banner on the dashboard, and open the knowledge base contents panel."
            : "You have not dismissed any tips or panels."}
        </Typography>
        <Button disabled={!tipsDismissed} onClick={showDismissedTips} variant="outlined">
          Show dismissed tips again
        </Button>
      </Stack>
    </SettingsSection>
  );
}
